import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Home, Building2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSelect } from "@/components/LanguageSelect";
import { Logo } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useAuth } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";
import { cn } from "@/lib/utils";

export default function Onboarding() {
  const { t } = useI18n();
  const { me, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const showErr = useErrorToast();
  const [type, setType] = useState("personal");
  const [name, setName] = useState("");
  const [code, setCode] = useState(sessionStorage.getItem("rangeo_invite") || "");
  const [busy, setBusy] = useState(false);
  if (me?.workspace) return <Navigate to="/" replace />;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); sessionStorage.removeItem("rangeo_invite"); await refresh(); navigate("/"); }
    catch (e) { showErr(e); } finally { setBusy(false); }
  };
  const create = (e) => { e.preventDefault(); run(() => api.post("/workspace", { name, type })); };
  const join = (e) => { e.preventDefault(); run(() => api.post("/workspace/join", { code })); };
  const types = [["personal", Home, "onb.personal", "onb.personalDesc"], ["business", Building2, "onb.business", "onb.businessDesc"]];

  return (
    <div className="min-h-screen grid-paper">
      <div className="flex items-center justify-between p-5 max-w-3xl mx-auto">
        <Logo />
        <div className="flex items-center gap-2">
          <LanguageSelect persist className="w-40" />
          <Button variant="ghost" size="sm" onClick={logout} data-testid="onboarding-logout">{t("nav.logout")}</Button>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-5 pb-16 space-y-8">
        <form onSubmit={create} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-6 fade-up">
          <div className="space-y-1">
            <h1 data-testid="onboarding-title" className="text-3xl sm:text-4xl font-extrabold">{t("onb.title")}</h1>
            <p className="text-slate-500">{t("onb.subtitle")}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {types.map(([v, Icon, k, d]) => (
              <button type="button" key={v} data-testid={`onboarding-type-${v}`} onClick={() => setType(v)}
                className={cn("text-start rounded-xl border-2 p-5 transition-colors duration-200", type === v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300")}>
                <Icon className={cn("w-6 h-6 mb-3", type === v ? "text-blue-600" : "text-slate-500")} />
                <p className="font-semibold">{t(k)}</p>
                <p className="text-sm text-slate-500">{t(d)}</p>
              </button>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>{t("onb.wsName")}</Label>
            <Input data-testid="onboarding-name-input" className="h-12" placeholder={t("onb.wsPlaceholder")} value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">{t("auth.trial")}</p>
          <Button data-testid="onboarding-create-button" type="submit" disabled={busy} className="h-12 w-full sm:w-auto px-6 gap-2 text-base">
            {t("onb.create")}<ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Button>
        </form>
        <form onSubmit={join} className="rounded-2xl border border-dashed border-slate-300 bg-white/70 p-6 space-y-4">
          <h2 className="text-lg font-bold">{t("onb.joinTitle")}</h2>
          <div className="flex gap-2">
            <Input data-testid="onboarding-code-input" className="h-12 font-mono uppercase" placeholder={t("onb.code")} value={code} onChange={(e) => setCode(e.target.value)} required />
            <Button data-testid="onboarding-join-button" type="submit" variant="outline" disabled={busy} className="h-12">{t("onb.join")}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
